import { useCallback } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { MetricRangeType } from "@waterly/graphql-types";

import { DEFAULT_PICKER_COLOR } from "../../ColorPicker/ColorPicker";
import type { MetricRangeCardMetricRange } from "../metricRangeUtils";

import LowerUpperLimitConfigInput from "./LowerUpperLimitConfigInput";
import type { MetricRangeTagOption } from "./MetricRangeTagSelect";
import type { RangeColorNameEmailDeleteControlProps } from "./RangeColorNameEmailDeleteControl";
import RangeColorNameEmailDeleteControl from "./RangeColorNameEmailDeleteControl";

export type LowerUpperLimitRangeConfigProps = {
  metricRangeType: MetricRangeType.Upper | MetricRangeType.Lower;
  range: MetricRangeCardMetricRange;
  onChange: (range: MetricRangeCardMetricRange) => void;
  onDelete: () => void;
  metricRangeTagOptions: MetricRangeTagOption[];
  bordersIdeal: boolean;
  max: number;
  min: number;
  devMode: boolean;
  nameErrorMessage?: string;
  limitErrorMessage?: string;
  disableName?: boolean;
  disableOperational?: boolean;
  disableRegulatory?: boolean;
};

const LowerUpperLimitRangeConfig = ({
  range,
  onChange,
  metricRangeType,
  onDelete,
  bordersIdeal,
  metricRangeTagOptions,
  nameErrorMessage,
  limitErrorMessage,
  max,
  min,
  devMode,
  disableName,
  disableOperational,
  disableRegulatory,
}: LowerUpperLimitRangeConfigProps) => {
  const isLower = metricRangeType === MetricRangeType.Lower;

  const handleNameChange = useCallback<RangeColorNameEmailDeleteControlProps["onNameChange"]>(
    (value) => {
      onChange({ ...range, name: value });
    },
    [onChange, range],
  );

  const handleColorChange = useCallback<RangeColorNameEmailDeleteControlProps["onColorChange"]>(
    (color) => {
      onChange({ ...range, color });
    },
    [onChange, range],
  );

  const handleEmailChange = useCallback<RangeColorNameEmailDeleteControlProps["onEmailChange"]>(
    (value) => onChange({ ...range, notificationEnabled: value }),
    [onChange, range],
  );

  return (
    <Stack>
      {!isLower && (
        <Box>
          <LowerUpperLimitConfigInput
            metricRangeType={MetricRangeType.Upper}
            range={range}
            metricRangeTagOptions={metricRangeTagOptions}
            onChange={onChange}
            bordersIdeal={bordersIdeal}
            min={min}
            max={max}
            errorMessage={limitErrorMessage}
            devMode={devMode}
            disableOperational={disableOperational}
            disableRegulatory={disableRegulatory}
          />
        </Box>
      )}
      <RangeColorNameEmailDeleteControl
        name={range.name}
        canDelete={!bordersIdeal}
        color={range.color ?? DEFAULT_PICKER_COLOR}
        enrolledInEmail={range.notificationEnabled}
        onNameChange={handleNameChange}
        onColorChange={handleColorChange}
        onDelete={onDelete}
        onEmailChange={handleEmailChange}
        nameErrorMessage={nameErrorMessage}
        disableName={disableName}
      />
      {isLower && (
        <Box>
          <LowerUpperLimitConfigInput
            metricRangeType={MetricRangeType.Lower}
            range={range}
            metricRangeTagOptions={metricRangeTagOptions}
            onChange={onChange}
            bordersIdeal={bordersIdeal}
            errorMessage={limitErrorMessage}
            min={min}
            max={max}
            devMode={devMode}
            disableOperational={disableOperational}
            disableRegulatory={disableRegulatory}
          />
        </Box>
      )}
    </Stack>
  );
};

export default LowerUpperLimitRangeConfig;
